import { Subject } from 'rxjs'
import { Objects } from './objects'

/**
 * Additional functionality related to the DOM.
 * @author Stan Hurks
 */
export default class DOM {
    public static temp = Objects.isArray([])

    /**
     * Whenever an observed DOM element has changed
     * Returns the ID of the observer.
     */
    public static readonly changed: Subject<number> = new Subject()

    /**
     * The counter for the ID of the observers
     */
    private static counter: number = 0

    /**
     * All observers
     */
    private static readonly observers: {[id: number]: {
        /**
         * The mutation observer
         */
        mutationObserver?: MutationObserver
        
        /**
         * The element that is observed
         */
        element?: Element

        /**
         * The callback registered for the observer
         */
        callback?: () => void
    }} = {}

    /**
     * Starts observing an element
     * @param element the element
     * @returns the ID of the DOM observer
     */
    public static observe = (element: Element): number|null => {
        const MutationObserver = window.MutationObserver || (window as any)['WebKitMutationObserver']
        const id = ++DOM.counter

        if (!element || element.nodeType !== 1) {
            return null
        }

        const callback = () => {
            DOM.changed.next(id)
        }

        if (MutationObserver) {
            const mutationObserver = new MutationObserver(callback)

            mutationObserver.observe(element, {
                childList: true,
                subtree: true
            })

            DOM.observers[id] = {
                mutationObserver
            }
        }
        else if (!!window.addEventListener) {
            element.addEventListener('DOMNodeInserted', callback, false)
            element.addEventListener('DOMNodeRemoved', callback, false)
            DOM.observers[id] = {
                element,
                callback
            }
        }
        else {
            return null
        }

        return DOM.counter
    }

    /**
     * Deactivate an observer.
     * @param id The ID of the observer
     */
    public static unobserve = (id: number) => {
        const observer = DOM.observers[id]
        if (observer) {
            if (observer.mutationObserver) {
                observer.mutationObserver.disconnect()
            }
            else if (observer.callback && observer.element) {
                observer.element.addEventListener('DOMNodeInserted', observer.callback, false)
                observer.element.addEventListener('DOMNodeRemoved', observer.callback, false)
            }
            delete DOM.observers[id]
        }
    }

    /**
     * Deactivate all observers.
     */
    public static unobserveAll = () => {
        for (const id of Object.keys(DOM.observers)) {
            DOM.unobserve(Number(id))
        }
    }
}